import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import lodashIdentity from 'lodash/fp/identity';
import lodashCompact from 'lodash/fp/compact';

import {
  hasRoleLandlord,
} from 'common/userRoles';

import {
  getPartnerSignupType,
} from 'common/partnerHelpers';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
  DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
} from 'common/dataTableConstants';

import { getUserLandingPage } from 'client/helpers/allowedRoles';

import {
  SCREEN_ID_PARTNER_LISTING,
} from 'client/constants/screenIds';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerListingPageListingFullService from 'client/components/SrPartnerListingPage/SrPartnerListingPageListingFullService';
import SrPartnerListingPageListingTenantScreening from 'client/components/SrPartnerListingPage/SrPartnerListingPageListingTenantScreening';
import SrPartnerListingPageListingSunrise from 'client/components/SrPartnerListingPage/SrPartnerListingPageListingSunRise';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import PartnerActions from 'client/actions/Partner';
import HelpCardActions from 'client/actions/HelpCard';
import PreLeaseActions from 'client/actions/PreLease';
import ConcessionAction from 'client/actions/Concession';
import PrequalificationActions from 'client/actions/Prequalifications';
import getListingLinksAction from 'client/actions/Listing/getLinks';

import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import {
  isListingSunRise,
  isListingTenantScreening,
} from 'common/listingHelpers';

function getListingDataTableSettings({ partnersBySlug, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  const partnerSlug = match.params.partnerSlug;
  const partner = partnersBySlug[partnerSlug];
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listings',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };

  return dataTableSettings;
}

function getMarketingListingDataTableSettings({ partnersBySlug, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  const partner = partnersBySlug[match.params.partnerSlug];
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'marketingListings',
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: `partner-id-${partner.id}-listing-id-${listingId}`,
    count: 1,
    filter: {
      'partners.slug': match.params.partnerSlug,
      'marketingPartnerListings.listingId': listingId,
    },
    sort: {
      'marketingPartnerListings.createdDate': 'desc',
    },
  };
  return dataTableSettings;
}

@withRouter
@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_LISTING)
@withScrollToTop
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  currentPartnerUser: ['partners', 'currentPartnerUser'],
  partnersBySlug: ['partners', 'bySlug'],
  listing: ['listing'],
  concessions: ['concessions', 'byListingId', match.params.listingId],
  appUrl: ['env', 'APP_URL'],
  listingLinksByListingId: ['listingLinksByListingId'],
  listingTourReviews: ['partners', 'listingTourReviewsById', match.params.listingId],
  leadOutcomes: ['partners', 'listingLeadOutcomes', match.params.listingId],
  householdsApplying: ['partners', 'householdsApplyingByListingId', match.params.listingId],
  householdsStartedApplying: ['partners', 'householdsStartedApplyingByListingId', match.params.listingId],
  prequalifications: ['prequalifications', 'byListingId', match.params.listingId],
  helpCards: ['helpCards', 'listByScreenId', SCREEN_ID_PARTNER_LISTING],
  preLease: ['preLeases', 'byListingId', match.params.listingId],
}))
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.helpCards = [
      'helpCards',
      'list',
      [SCREEN_ID_PARTNER_LISTING, getPartnerSignupType(partner)].join('-'),
    ];
  }

  return cursors;
})
@dataTable(getMarketingListingDataTableSettings)
@dataTable(getListingDataTableSettings)
class PartnerListingPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Listing | Sunroom Rentals',
    section: 'Partner',
  })

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    partnersBySlug: PropTypes.object,
    listings: PropTypes.array,
    marketingListings: PropTypes.array,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    householdsApplying: PropTypes.object,
    householdsStartedApplying: PropTypes.object,
    location: PropTypes.object.isRequired,
    helpCards: PropTypes.array,
  }

  static fetchData(match, inject) {
    const partnersBySlug = inject.state.get(['partners', 'bySlug']);
    const partner = partnersBySlug[match.params.partnerSlug];
    const listingId = parseInt(match.params.listingId, 10);
    const promises = lodashCompact([
      PartnerActions.getHouseholdsApplyingToListing(inject, {
        listingId,
        forceRefresh: true,
        includeAllHouseholdDataToChecklist: true,
      }),
      PartnerActions.getHouseholdsStartedApplyingToListing(inject, {
        listingId,
        forceRefresh: true,
      }),
      PartnerActions.getListingLeadOutcomes(inject, {
        listingId,
        forceRefresh: true,
      }),
      PartnerActions.getPartnerListingTourReviews(inject, { listingId, forceRefresh: true }),
      PrequalificationActions.getByListingId(inject, { listingId }),
      getListingLinksAction(inject, { listingId }),
      PreLeaseActions.getByListingId(inject, { listingId, forceRefresh: true }),
      ConcessionAction.getByListingId(inject, {
        listingId: parseInt(match.params.listingId, 10),
        forceReload: true,
      }),
    ]);

    [
      getListingDataTableSettings({ partnersBySlug, match }),
      getMarketingListingDataTableSettings({ partnersBySlug, match }),
    ]
      .filter(lodashIdentity)
      .forEach((dataTableSettings) => {
        actionInitializeDataTableState(inject, dataTableSettings);
        promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));
      });

    if (!partner) {
      promises.push(PartnerActions.loadPartnerBySlug(inject, {
        slug: match.params.partnerSlug,
      }));
    }

    return Promise.all(promises).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
      return HelpCardActions.getList(inject, {
        screenId: SCREEN_ID_PARTNER_LISTING,
        partnerSignupType: getPartnerSignupType(partner),
      });
    });
  }

  componentDidMount() {
    this._reloadPartnerListing();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { partnersBySlug } = nextProps;
    if (
      Object.keys(this.props.partnersBySlug).length
      !== Object.keys(partnersBySlug).length
    ) {
      this._reloadPartnerListing(nextProps);
    }
  }

  _reloadPartnerListing = (props) => {
    const { partnersBySlug, match, dispatch } = props || this.props;

    const listingId = parseInt(match.params.listingId, 10);

    const dataTablesPromises = [
      getListingDataTableSettings({ partnersBySlug, match }),
      getMarketingListingDataTableSettings({ partnersBySlug, match }),
    ]
      .filter(lodashIdentity)
      .map((dataTableSettings) => {
        return dispatch(actionDataTableLoadItems, dataTableSettings);
      });

    return Promise.all([
      ...dataTablesPromises,
      dispatch(PartnerActions.getHouseholdsApplyingToListing, {
        listingId,
        forceRefresh: true,
        includeAllHouseholdDataToChecklist: true,
      }),
      dispatch(PreLeaseActions.getByListingId, {
        listingId,
        forceRefresh: true,
      }),
    ]);
  }

  _getPageComponentSpecificProps = (component) => {
    const {
      listings,
      marketingListings,
      householdsApplying,
      listingTourReviews,
      householdsStartedApplying,
      prequalifications,
      leadOutcomes,
      concessions,
      preLease,
    } = this.props;

    switch (component) {
      case SrPartnerListingPageListingTenantScreening: {
        const listing = listings && listings[0] || null;
        return {
          listing: listing,
          listingTourReviews: listingTourReviews,
          householdsApplying: householdsApplying,
          householdsStartedApplying: householdsStartedApplying,
          prequalifications: prequalifications,
        };
      }
      case SrPartnerListingPageListingSunrise: {
        const listing = listings && listings[0] || null;
        const marketingListing = marketingListings && marketingListings[0] || null;
        return {
          marketingListing: marketingListing,
          listing: listing,
        };
      }
      case SrPartnerListingPageListingFullService:
      default:
        return {
          listings: listings,
          marketingListings: marketingListings,
          householdsApplying: householdsApplying,
          listingTourReviews: listingTourReviews,
          leadOutcomes: leadOutcomes,
          preLease: preLease,
          concessions: concessions,
        };
    }
  }

  _getPartnerListingPageComponent = (listing) => {
    if (!listing || !listing.services) {
      return;
    }

    if (isListingTenantScreening(listing)) {
      return SrPartnerListingPageListingTenantScreening;
    }
    if (isListingSunRise(listing)) {
      return SrPartnerListingPageListingSunrise;
    }
    return SrPartnerListingPageListingFullService;
  }

  _getPartnerListingPage = (listing) => {
    if (!listing) {
      return;
    }

    const {
      match,
      dispatch,
      history,
      location,
      helpCards,
    } = this.props;

    const PartnerListingPage = this._getPartnerListingPageComponent(listing);
    const componentSpecificProps = this._getPageComponentSpecificProps(PartnerListingPage);

    return (
      <PartnerListingPage
        match={match}
        history={history}
        dispatch={dispatch}
        location={location}
        helpCards={helpCards}
        reloadPartnerListing={this._reloadPartnerListing}
        {...componentSpecificProps}
      />
    );
  }

  _onBack = () => {
    const {
      history,
      match,
    } = this.props;

    history.push(`/partner/${match.params.partnerSlug}`);
  }

  render() {
    const {
      currentUser,
      partnersBySlug,
      listings,
      dataTableIsFullyLoaded,
      match,
    } = this.props;

    const currentPartner = partnersBySlug[match.params.partnerSlug];
    const listing = listings && listings[0] || null;
    const isLandlord = hasRoleLandlord(currentUser.roles);

    if (dataTableIsFullyLoaded && !listing) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return (
        <Redirect to={userLandingPage} />
      );
    }

    const PartnerListingPage = this._getPartnerListingPage(listing);
    return (
      <div className='SrPartnerPage PartnerListingPage'>
        <SrPartnerPageV2
          onBack={isLandlord ? null : this._onBack}
          navbarTitle={currentPartner?.displayName}
          navbarImage={currentPartner?.logoUrl}
          padding='0'
          contentPadding='0'
          maxWidth='unset'
          navbarMaxWidth='1180px'
          currentPartner={currentPartner}
          currentUser={isLandlord ? null : currentUser}
        >
          {!dataTableIsFullyLoaded && !listing && (
            <div className='SrPartnerPageContent'>
              <div className='loading-ellipses' />
            </div>
          )}
          {PartnerListingPage}
        </SrPartnerPageV2>
      </div>
    );
  }
}

PartnerListingPage.displayName = 'PartnerListingPage';

export default PartnerListingPage;
